import { defineMessages, type IntlShape } from 'react-intl';
import { type SourceControlToggleState } from '@/atoms/sourceControlAtoms';;
import { type useAvailableIntegrations } from '@/components/chat/hooks/useAvailableIntegrations';
import type { useCollections } from '@heidi/evidence/data';
import { isEvidenceEligibleCollection } from '@heidi/evidence/contracts';
import type { useSourceLists } from '@heidi/evidence/data';
import { myLibrarySubmenuMessages } from './components/my-library-submenu.messages';

export interface PersonalisedSource {
  key: string;
  displayName: string;
  logoUrl?: string;
  domain?: string;
  brandColor?: string;
  // `null` = collection without a user-picked icon (renders the default
  // CollectionIcon glyph). `undefined` = not a collection at all.
  iconName?: string | null;
}

export type PersonalisedSourceFaceItem =
  | { kind: 'source'; source: PersonalisedSource }
  | { kind: 'category'; category: SourceTypeCategory };

export interface PersonalisedSourceFaceSelection {
  items: PersonalisedSourceFaceItem[];
  trailingLabel: string | null;
}

const MAX_VISIBLE_SOURCE_FACES = 3;
const DEFAULT_SOURCE_FACE_LABEL = '+7k';

// The default Sources stack shows three marks. Two partners that share a
// logo_url (ClinicalKey used to reuse Elsevier's favicon) must not both
// appear — skip the repeat and take the next source instead.
export const takeDistinctLogoSources = (
  sources: readonly PersonalisedSource[],
  max: number
): PersonalisedSource[] => {
  const seen = new Set<string>();
  const visible: PersonalisedSource[] = [];
  for (const source of sources) {
    const mark = source.logoUrl;
    if (mark && seen.has(mark)) continue;
    if (mark) seen.add(mark);
    visible.push(source);
    if (visible.length >= max) break;
  }
  return visible;
};

/** Shared Evidence and Work selection rules; callers only choose item presentation. */
export const selectPersonalisedSourceFace = (
  summary: SourceSelectionSummary,
  selectionTotal: number,
  partnerSources: readonly PersonalisedSource[],
  defaultSources: readonly PersonalisedSource[],
  librarySources: readonly PersonalisedSource[],
  variant: 'evidence' | 'work',
  hideScaleLabel = false
): PersonalisedSourceFaceSelection => {
  let items: PersonalisedSourceFaceItem[];
  const hasSelectedLibrary = librarySources.length > 0;
  const hasSelectedSources = partnerSources.length > 0 || hasSelectedLibrary;
  if (summary.isDefault) {
    items = takeDistinctLogoSources(defaultSources, MAX_VISIBLE_SOURCE_FACES).map(
      (source) => ({ kind: 'source', source })
    );
  } else if (variant === 'work') {
    return selectWorkNarrowedFace(
      summary,
      partnerSources,
      defaultSources,
      hideScaleLabel
    );
  } else if (hasSelectedSources) {
    items = [...partnerSources, ...librarySources]
      .slice(0, MAX_VISIBLE_SOURCE_FACES)
      .map((source) => ({ kind: 'source', source }));
  } else {
    items = summary.activeTypes
      .slice(0, MAX_VISIBLE_SOURCE_FACES)
      .map((category) => ({ kind: 'category', category }));
  }

  const shownCount = items.length;
  const trailingCount = selectionTotal;
  const trailingLabel = hideScaleLabel
    ? null
    : summary.isDefault
      ? DEFAULT_SOURCE_FACE_LABEL
      : trailingCount - shownCount > 0
        ? `+${trailingCount - shownCount}`
        : null;

  return { items, trailingLabel };
};

// Picked partners show their own logos, else the default stack; any library
// selection takes the last slot. "+N" counts unshown partners; without picks,
// "+7k" shows while Clinical web runs, since other groups only add to it.
const selectWorkNarrowedFace = (
  summary: SourceSelectionSummary,
  partnerSources: readonly PersonalisedSource[],
  defaultSources: readonly PersonalisedSource[],
  hideScaleLabel: boolean
): PersonalisedSourceFaceSelection => {
  const picked = partnerSources;
  const showsLibraryIcon = summary.activeTypes.includes('myLibrary');
  const logoSlots = showsLibraryIcon
    ? MAX_VISIBLE_SOURCE_FACES - 1
    : MAX_VISIBLE_SOURCE_FACES;
  const logos =
    picked.length > 0
      ? picked.slice(0, logoSlots)
      : takeDistinctLogoSources(defaultSources, logoSlots);
  // A lone library icon would read as a picked library, not a loading face.
  if (logos.length === 0) return { items: [], trailingLabel: null };
  const items: PersonalisedSourceFaceItem[] = logos.map((source) => ({
    kind: 'source',
    source,
  }));
  if (showsLibraryIcon) items.push({ kind: 'category', category: 'myLibrary' });

  if (hideScaleLabel) return { items, trailingLabel: null };
  if (picked.length === 0) {
    return {
      items,
      trailingLabel: summary.activeTypes.includes('clinicalWeb')
        ? DEFAULT_SOURCE_FACE_LABEL
        : null,
    };
  }
  const hiddenPicks = picked.length - Math.min(picked.length, logoSlots);
  return { items, trailingLabel: hiddenPicks > 0 ? `+${hiddenPicks}` : null };
};

// `null` filter = unfiltered browse view. `collectionOk` gets `hasDomains`
// because a collection can land in either selection bucket.
export const buildSourceList = (
  partners: ReturnType<typeof useAvailableIntegrations>['partners'],
  sourceLists: ReturnType<typeof useSourceLists>['data'],
  collections: ReturnType<typeof useCollections>['data'],
  filter: {
    partnerOk: (partnerId: string) => boolean;
    listOk: (listId: string) => boolean;
    collectionOk: (collectionId: string, hasDomains: boolean) => boolean;
  } | null
): PersonalisedSource[] => {
  const list: PersonalisedSource[] = [];

  for (const partner of partners) {
    if (filter && !filter.partnerOk(partner.id)) continue;
    list.push({
      key: `partner:${partner.id}`,
      displayName: partner.displayName,
      logoUrl: partner.logo,
      brandColor: partner.brandColor,
    });
  }

  const seenDomains = new Set<string>();
  for (const collection of collections?.collections ?? []) {
    if (!isEvidenceEligibleCollection(collection)) continue;
    const domains = collection.domains ?? [];
    const hasDomains = domains.length > 0;
    if (filter && !filter.collectionOk(collection.collection_id, hasDomains)) {
      continue;
    }
    if (hasDomains) {
      for (const domain of domains) {
        if (!domain || seenDomains.has(domain)) continue;
        seenDomains.add(domain);
        list.push({
          key: `domain:${domain}`,
          displayName: domain,
          domain,
        });
      }
    } else {
      list.push({
        key: `collection:${collection.collection_id}`,
        displayName: collection.name,
        iconName: collection.icon ?? null,
      });
    }
  }

  for (const sl of sourceLists ?? []) {
    if (filter && !filter.listOk(sl.id)) continue;
    for (const domain of sl.domains) {
      if (!domain || seenDomains.has(domain)) continue;
      seenDomains.add(domain);
      list.push({
        key: `domain:${domain}`,
        displayName: domain,
        domain,
      });
    }
  }

  return list;
};

const ON_DARK = 'text-text-primary-on-dark';
const ON_LIGHT = 'text-text-primary-on-light';
const HEX_PATTERN = /^#?([\da-f]{3}|[\da-f]{6}|[\da-f]{8})$/i;

// YIQ luminance pick (threshold 128); falls back to on-dark for unparseable input.
export const getBrandTextClass = (
  brandColor: string | undefined
): typeof ON_DARK | typeof ON_LIGHT => {
  const hex = brandColor?.match(HEX_PATTERN)?.[1];
  if (!hex) return ON_DARK;
  const full =
    hex.length === 3 ? hex.replace(/./g, (c) => c + c) : hex.slice(0, 6);
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 >= 128 ? ON_LIGHT : ON_DARK;
};

// Coarse "source type" categories (everything that isn't an individual
// partner integration). Order here is the display order in the button.
export type SourceTypeCategory =
  | 'clinicalWeb'
  | 'literatureGuidelines'
  | 'myLibrary';

export interface SourceSelectionSummary {
  /** Selected partner integrations (only counts currently available partners). */
  partnerCount: number;
  /** Active non-partner source-type categories, in display order. */
  activeTypes: SourceTypeCategory[];
  /** `activeTypes.length` */
  typeCount: number;
  /** `partnerCount + typeCount` — the coarse "sources selected" total. */
  total: number;
  /**
   * True when nothing that narrows the corpus is selected. Clinical web
   * (`webSearch`) alone still counts as default: it's a filter over the
   * default browse corpus, not an added source.
   */
  isDefault: boolean;
}

// Coarse selection summary for the personalised Sources button. Each selected
// partner counts as 1 and each active source-type category counts as 1 (so
// 3 partners + 3 types === "6 sources selected"), matching the button design.
export const summariseSourceSelection = (
  toggleState: SourceControlToggleState,
  availablePartnerIds: readonly string[]
): SourceSelectionSummary => {
  const partnerCount = availablePartnerIds.reduce(
    (count, id) =>
      toggleState.integrationPartners[id] === true ? count + 1 : count,
    0
  );

  const activeTypes: SourceTypeCategory[] = [];
  if (toggleState.webSearch) activeTypes.push('clinicalWeb');
  if (toggleState.localGuidelines || toggleState.primaryEvidence) {
    activeTypes.push('literatureGuidelines');
  }
  if (toggleState.myLibrary || toggleState.webCollections) {
    activeTypes.push('myLibrary');
  }

  const typeCount = activeTypes.length;
  const total = partnerCount + typeCount;
  const isDefault =
    partnerCount === 0 &&
    !toggleState.localGuidelines &&
    !toggleState.primaryEvidence &&
    !toggleState.myLibrary &&
    !toggleState.webCollections;

  return { partnerCount, activeTypes, typeCount, total, isDefault };
};

// IDs of the specific collections the user has picked (library "collections"
// mode and/or web-collections mode). Empty when only whole buckets are enabled
// without narrowing to a collection.
const getSelectedCollectionIds = (
  toggleState: SourceControlToggleState
): Set<string> => {
  const ids = new Set<string>();
  if (toggleState.myLibrary && toggleState.libraryMode === 'collections') {
    for (const id of toggleState.selectedCollectionIds) ids.add(id);
  }
  if (
    toggleState.webCollections &&
    toggleState.webCollectionsMode === 'collections'
  ) {
    for (const id of toggleState.selectedWebCollectionIds) ids.add(id);
  }
  return ids;
};

// The rendered logos for the sources inside the picked collection(s):
// domain-backed collections contribute one entry per de-duped domain; doc-only
// collections contribute a single glyph entry. Used for the avatar row (sliced
// to maxVisible). `buildSourceList` de-dupes domains across collections/lists.
export const buildSelectedLibrarySources = (
  toggleState: SourceControlToggleState,
  sourceLists: ReturnType<typeof useSourceLists>['data'],
  collections: ReturnType<typeof useCollections>['data']
): PersonalisedSource[] => {
  const ids = getSelectedCollectionIds(toggleState);
  if (ids.size === 0) return [];
  return buildSourceList([], sourceLists, collections, {
    partnerOk: () => false,
    listOk: (listId) => ids.has(listId),
    collectionOk: (collectionId) => ids.has(collectionId),
  });
};

// Whole-bucket selections (library/web on, mode not narrowed to a collection)
// count every eligible pack, not just the ids the user picked.
const isBroadLibrarySelection = (
  toggleState: SourceControlToggleState
): boolean =>
  toggleState.myLibrary && toggleState.libraryMode !== 'collections';

const isBroadWebSelection = (toggleState: SourceControlToggleState): boolean =>
  toggleState.webCollections && toggleState.webCollectionsMode !== 'collections';

// The true number of sources inside the picked collection(s) — every document
// AND every web domain in a collection counts as one source (min 1 so a
// still-loading pack never reads as empty). A broad, un-narrowed library or
// web-collections bucket uses the same fields summed across the whole eligible
// corpus. This can exceed the rendered logo count: doc-only collections show a
// single glyph but may hold many documents, and list-response collections don't
// always expand their domains. Feeds the announced selection total (aria-label
// and tooltip), not a visible overflow chip, so that count reflects the real
// corpus, not just the visible avatars.
export const countLibraryCollectionSources = (
  toggleState: SourceControlToggleState,
  sourceLists: ReturnType<typeof useSourceLists>['data'],
  collections: ReturnType<typeof useCollections>['data'],
  variant: 'evidence' | 'work' = 'evidence'
): number => {
  const ids = getSelectedCollectionIds(toggleState);
  const countAllLibrary =
    variant === 'work' && isBroadLibrarySelection(toggleState);
  const countAllWeb = variant === 'work' && isBroadWebSelection(toggleState);
  if (ids.size === 0 && !countAllLibrary && !countAllWeb) return 0;

  let count = 0;
  for (const collection of collections?.collections ?? []) {
    if (!isEvidenceEligibleCollection(collection)) continue;
    if (!countAllLibrary && !ids.has(collection.collection_id)) continue;
    const domainCount =
      collection.domain_count ?? collection.domains?.length ?? 0;
    count += Math.max(1, collection.document_count + domainCount);
  }
  for (const sourceList of sourceLists ?? []) {
    if (!countAllWeb && !ids.has(sourceList.id)) continue;
    count += Math.max(1, sourceList.domains.length);
  }
  return count;
};

// The "sources selected" count for the personalised Sources button's
// accessible label and tooltip ("n sources selected"). Not rendered as a
// visible chip. Mirrors the avatar-display precedence: whenever a collection
// is selected, or a library/web bucket is on without narrowing, each
// individual source counts as 1 (`libraryCount`), plus each partner and each
// *other* active source-type category — so a "2 partners + library" selection
// counts the partners AND the corpus, not a coarse "myLibrary === 1".
// Otherwise it falls back to the coarse summary total (each partner + each
// active source-type category === 1). `isDefault` stays on that coarse total
// (the default face is a logo stack, not a counted corpus).
export const countDisplayedSourceTotal = (
  summary: SourceSelectionSummary,
  librarySources: readonly PersonalisedSource[],
  libraryCount: number,
  variant: 'evidence' | 'work' = 'evidence'
): number => {
  if (summary.isDefault) return summary.total;
  if (librarySources.length > 0 || (variant === 'work' && libraryCount > 0)) {
    const otherTypeCount = summary.activeTypes.filter(
      (type) => type !== 'myLibrary'
    ).length;
    // Never report fewer than the logos actually shown (guards against stale /
    // zeroed collection metadata undercounting the visible avatars).
    const sources = Math.max(libraryCount, librarySources.length);
    return sources + otherTypeCount + summary.partnerCount;
  }
  return variant === 'work'
    ? summary.partnerCount + summary.typeCount
    : summary.total;
};

export const personalisedSourceTooltipMessages = defineMessages({
  defaultTooltip: {
    defaultMessage: 'Filter sources',
    id: 'lFEAGCxT8w',
    description:
      'Tooltip for the personalised Sources button in the default browse state',
  },
  guidelinesOnly: {
    defaultMessage: 'Guidelines & guidance only',
    id: '4AMxKFKePS',
    description:
      'Name of the Sources filter that limits Evidence web search to formal guidelines and guidance; shown on its toggle and in the Sources button tooltip',
  },
  partnerSources: {
    defaultMessage:
      '{count, plural, one {# partner source} other {# partner sources}}',
    id: 'qT7vKp2mWs',
    description:
      'Part of the personalised Sources button tooltip naming how many partner sources the user picked',
  },
});

/** Names each active filter, since a narrowed Work face can look like the default stack. */
export const formatPersonalisedSourceTooltip = (
  intl: IntlShape,
  summary: SourceSelectionSummary
): string => {
  if (summary.isDefault) {
    return intl.formatMessage(personalisedSourceTooltipMessages.defaultTooltip);
  }
  const parts: string[] = [];
  if (summary.activeTypes.includes('literatureGuidelines')) {
    parts.push(intl.formatMessage(personalisedSourceTooltipMessages.guidelinesOnly));
  }
  if (summary.activeTypes.includes('myLibrary')) {
    parts.push(intl.formatMessage(myLibrarySubmenuMessages.myLibrary));
  }
  if (summary.partnerCount > 0) {
    parts.push(
      intl.formatMessage(personalisedSourceTooltipMessages.partnerSources, {
        count: summary.partnerCount,
      })
    );
  }
  const [onlyPart] = parts;
  return parts.length === 1 && onlyPart !== undefined
    ? onlyPart
    : intl.formatList(parts, { type: 'conjunction' });
};
